import { createContext, useCallback, useContext, useMemo, useRef, type ReactNode } from 'react';

type EditorDatabaseViewPickerContextValue = {
  registerOpen: (open: () => void) => () => void;
  open: () => void;
};

const EditorDatabaseViewPickerContext =
  createContext<EditorDatabaseViewPickerContextValue | null>(null);

export function EditorDatabaseViewPickerProvider({ children }: { children: ReactNode }) {
  const openerRef = useRef<(() => void) | null>(null);

  const registerOpen = useCallback((open: () => void) => {
    openerRef.current = open;
    return () => {
      if (openerRef.current === open) {
        openerRef.current = null;
      }
    };
  }, []);

  const open = useCallback(() => {
    openerRef.current?.();
  }, []);

  const value = useMemo(() => ({ registerOpen, open }), [open, registerOpen]);

  return (
    <EditorDatabaseViewPickerContext.Provider value={value}>
      {children}
    </EditorDatabaseViewPickerContext.Provider>
  );
}

export function useEditorDatabaseViewPicker(): EditorDatabaseViewPickerContextValue {
  const ctx = useContext(EditorDatabaseViewPickerContext);
  if (!ctx) {
    throw new Error(
      'useEditorDatabaseViewPicker must be used within EditorDatabaseViewPickerProvider',
    );
  }
  return ctx;
}
